import {
  CopyOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Button, Space, TimePicker, Typography, Input } from "antd";
import React, { useState, useEffect } from "react";
import { InterviewSettingsAvailableSlot } from "../../../Entities/InterviewSettings";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { FormProps } from "../FormProps";
import TourOverlay from "../../../Components/Tour/TourOverlay";

dayjs.extend(utc);
dayjs.extend(timezone);

const AvailabilityForm: React.FC<FormProps> = ({
  settings,
  updateSettings,
  timeZone,
}) => {
  const defaultMondaySlot = {
    day: 1, // Monday
    fromTime: "9:00 AM",
    toTime: "6:00 PM",
    unavailable: false,
    isSecondary: false,
  };
  const availableSlots = settings?.availableSlots || [defaultMondaySlot];
  const availableSlot = availableSlots?.map((slot) => ({
    day: slot?.day,
    fromTime: dayjs(slot?.fromTime).tz(timeZone).format("h:mm A"),
    toTime: dayjs(slot?.toTime).tz(timeZone).format("h:mm A"),
    unavailable: false,
    isSecondary: false,
  }));

  const [slots, setSlots] = useState(availableSlot);
  const [errors, setErrors] = useState<{ [key: number]: string }>({});

  const daysOfWeek = [0, 1, 2, 3, 4, 5, 6];

  useEffect(() => {
    const availableSlot =
      availableSlots?.length > 0
        ? availableSlots?.map((slot) => ({
            day: slot.day,
            fromTime: dayjs(slot?.fromTime).tz(timeZone).format("h:mm A"),
            toTime: dayjs(slot?.toTime).tz(timeZone).format("h:mm A"),
            unavailable: false,
            isSecondary: false,
          }))
        : [defaultMondaySlot];

    const processedDays = new Set();

    const updatedDefaultSlots = daysOfWeek.flatMap((day) => {
      const matchingSlots = availableSlot.filter((s) => s.day === day);

      if (matchingSlots?.length > 0) {
        return matchingSlots?.map((slot, index) => {
          const isSecondary = processedDays.has(day) || index > 0;
          return {
            ...slot,
            isSecondary,
          };
        });
      }

      const defaultSlot = {
        day,
        fromTime: "",
        toTime: "",
        unavailable: availableSlots?.length === 0 || true,
        isSecondary: false,
      };

      processedDays.add(day);
      return defaultSlot;
    });

    setSlots(updatedDefaultSlots);
  }, [settings, timeZone]);

  const handleChange = (
    index: number,
    field: "fromTime" | "toTime",
    value: dayjs.Dayjs | null,
    slot: {
      day: number;
      fromTime: string;
      toTime: string;
      unavailable: boolean;
      isSecondary: boolean;
    }
  ) => {
    if (slots[index] && !slots[index].unavailable) {
      const newSlots = [...slots];
      const formattedTime = value ? value.format("h:mm A") : "";

      const newErrors = { ...errors };

      if (field === "fromTime" && value && slot.isSecondary) {
        const previousSlotToTime = slots[index - 1].toTime;
        const toTime = dayjs(slots[index].toTime, "h:mm A");
        const fromTime = value;
        if (
          fromTime.isValid() &&
          fromTime.isBefore(dayjs(previousSlotToTime, "h:mm A"))
        ) {
          newErrors[index] = "Time should greater than previous slots totime.";
          setErrors(newErrors);
          return;
        } else if (fromTime.isValid() && fromTime.isAfter(toTime)) {
          newErrors[index] = "Select a valid time interval..";
          setErrors(newErrors);
          return;
        } else {
          delete newErrors[index];
        }
      }

      newSlots[index] = {
        ...newSlots[index],
        [field]: formattedTime,
      };

      const currentFromTime =
        field === "fromTime" ? formattedTime : newSlots[index].fromTime;
      const currentToTime =
        field === "toTime" ? formattedTime : newSlots[index].toTime;

      if (currentFromTime && currentToTime) {
        const fromTime = dayjs(currentFromTime, "h:mm A");
        const toTime = dayjs(currentToTime, "h:mm A");

        if (fromTime.isValid() && toTime.isValid()) {
          if (fromTime.isAfter(toTime)) {
            newErrors[index] = "Start time must be before end time.";
          } else {
            const timeDifferenceInMinutes = toTime.diff(fromTime, "minute");
            if (timeDifferenceInMinutes < 30) {
              newErrors[index] = "Time interval must be at least 30 minutes.";
            } else {
              delete newErrors[index];
            }
          }
        }
      } else {
        delete newErrors[index];
      }

      setSlots(newSlots);
      setErrors(newErrors);
    }
  };

  const handleAdd = (day: number) => {
    const index = slots
      .slice()
      .reverse()
      .findIndex((s) => s.day === day);
    if (index === -1) return;
    const adjustedIndex = slots?.length - 1 - index;
    const newSlots = [...slots];
    newSlots.splice(adjustedIndex + 1, 0, {
      day: newSlots[adjustedIndex].day,
      fromTime: "",
      toTime: "",
      unavailable: false,
      isSecondary: true,
    });
    setSlots(newSlots);
  };

  const handleRemove = (index: number) => {
    if (slots?.length > 1) {
      const newSlots = slots.filter((_, i) => i !== index);
      errors[index] && delete errors[index];
      setSlots(newSlots);
    }
  };

  const handleMarkEntireDayUnavailable = (index: number) => {
    if (
      slots?.length === 1 &&
      slots[0].day === 1 &&
      slots[0].fromTime === "9:00 AM" &&
      slots[0].toTime === "6:00 PM"
    ) {
      return;
    }
    const targetDay = slots[index].day;

    const newSlots = slots
      ?.filter((slot) => slot.day !== targetDay || !slot.isSecondary)
      ?.map((slot, i) =>
        i === index
          ? {
              ...slot,
              fromTime: "",
              toTime: "",
              unavailable: true,
              isSecondary: false,
            }
          : slot
      );
    errors[index] && delete errors[index];
    setSlots(newSlots);
  };

  const handleEnableDay = (index: number) => {
    const newSlots = [...slots];
    newSlots[index] = {
      ...newSlots[index],
      unavailable: false,
    };
    setSlots(newSlots);
  };

  const handleApplyToAllSlots = (index: number) => {
    const targetSlot = slots[index];

    if (targetSlot.unavailable || !targetSlot.fromTime || !targetSlot.toTime) {
      return;
    }

    const targetSecondarySlots = slots.filter(
      (slot) => slot.day === targetSlot.day && slot.isSecondary
    );

    let newSlots = slots
      ?.map((slot) =>
        slot?.unavailable || slot?.isSecondary
          ? slot
          : {
              ...slot,
              fromTime: targetSlot?.fromTime,
              toTime: targetSlot?.toTime,
            }
      )
      .filter((slot) => !(slot.isSecondary && slot.day !== targetSlot.day));

    if (targetSecondarySlots?.length > 0) {
      const finalSlots: any = [];
      newSlots.forEach((slot) => {
        finalSlots.push(slot);
        if (
          slot.day !== targetSlot.day &&
          !slot.isSecondary &&
          !slot.unavailable
        ) {
          const slotsToInsert = targetSecondarySlots.map((targetSlot) => ({
            ...targetSlot,
            day: slot.day,
          }));
          finalSlots.push(...slotsToInsert);
        }
      });
      newSlots = finalSlots;
    } else {
      let finalSlots: any = newSlots.filter((slot) => !slot.isSecondary);
      newSlots = finalSlots;
    }

    setSlots(newSlots);
  };

  const handleSave = async () => {
    if (Object.keys(errors)?.length > 0) {
      return;
    }

    const selectedDaySlots = slots.filter(
      (slot) => !slot.unavailable && slot.fromTime && slot.toTime
    );

    const formatedSlots: InterviewSettingsAvailableSlot[] = [];

    selectedDaySlots.forEach((slot) => {
      if (slot.fromTime && slot.toTime) {
        const fromTime = dayjs
          .tz(slot.fromTime, "h:mm A", timeZone)
          .utc()
          .toISOString();
        const toTime = dayjs
          .tz(slot.toTime, "h:mm A", timeZone)
          .utc()
          .toISOString();

        formatedSlots.push({
          day: slot.day,
          fromTime: fromTime,
          toTime: toTime,
        });
      }
    });

    if (formatedSlots?.length > 0 && settings) {
      settings.availableSlots = formatedSlots;
      await updateSettings(settings, true, false);
    } else if (formatedSlots?.length === 0 && settings) {
      settings.availableSlots = [];
      await updateSettings(settings, true, false);
    }
  };

  const availableSlotsLength = () => {
    const uniqueDays = new Set<number>();
    slots?.forEach((slot) => {
      if (!slot.unavailable && slot.fromTime && slot.toTime) {
        uniqueDays.add(slot.day);
      }
    });
    return uniqueDays?.size;
  };

  return (
    <Space direction="vertical" size="large" className="interview-settings-container">
      <TourOverlay targetId="general-availability-section">
        <Typography.Title level={5} style={{ marginTop: 3 }}>
          General Availability
        </Typography.Title>
        <Space direction="vertical" size="small">
          {slots?.map((slot, index) => (
            <Space key={index} direction="vertical" size="small">
              <Space align="center" size="small">
                {!slot.isSecondary && (
                  <Input
                    disabled
                    value={
                      [
                        "Sunday",
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                      ][slot.day]
                    }
                    style={{ width: 100 }}
                    size="small"
                  />
                )}

                <TimePicker
                  format="h:mm A"
                  minuteStep={15}
                  value={slot.fromTime ? dayjs(slot.fromTime, "h:mm A") : null}
                  onChange={(value) =>
                    handleChange(index, "fromTime", value, slot)
                  }
                  allowClear
                  disabled={slot.unavailable}
                  showNow={false}
                  needConfirm={false}
                  style={{ marginLeft: slot.isSecondary ? 108 : 0 }}
                />

                <TimePicker
                  format="h:mm A"
                  minuteStep={15}
                  value={slot.toTime ? dayjs(slot.toTime, "h:mm A") : null}
                  onChange={(value) =>
                    handleChange(index, "toTime", value, slot)
                  }
                  allowClear
                  showNow={false}
                  needConfirm={false}
                  disabled={slot.unavailable}
                />

                {!slot.unavailable && slot.isSecondary && (
                  <Button
                    type="dashed"
                    onClick={() => handleRemove(index)}
                    icon={<MinusCircleOutlined />}
                  />
                )}

                {!slot.unavailable &&
                  !slot.isSecondary &&
                  availableSlotsLength() > 1 && (
                    <Button
                      type="dashed"
                      onClick={() => handleMarkEntireDayUnavailable(index)}
                      icon={<StopOutlined />}
                    />
                  )}

                {!slot.isSecondary && (
                  <Button
                    type="dashed"
                    onClick={() =>
                      slot.unavailable
                        ? handleEnableDay(index)
                        : handleAdd(slot.day)
                    }
                    icon={<PlusOutlined />}
                  />
                )}
                {!slot.isSecondary && !slot.unavailable && (
                  <Button
                    type="dashed"
                    onClick={() => handleApplyToAllSlots(index)}
                    icon={<CopyOutlined />}
                  />
                )}
              </Space>
              {errors[index] && (
                <Typography.Text type="danger">{errors[index]}</Typography.Text>
              )}
            </Space>
          ))}
        </Space>
      </TourOverlay>
      <Space style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="primary"
          onClick={handleSave}
          disabled={Object.keys(errors)?.length > 0}
        >
          Save Changes
        </Button>
      </Space>
    </Space>
  );
};

export default AvailabilityForm;
